// utils/builtInApplets.js
//
// Single source of truth for the "Built-In Applets" nav block — which built-ins
// an org exposes, and which of those the signed-in user may actually reach.
//
// Shared by the main Sidebar and the dashboard shortcut picker, the same way
// `houseNav.js` is shared. Keeping one list here matters more than usual: the
// access rules are spread across module-hide flags, account type, org account
// type, admin status and two hardcoded user lists. A second copy of that logic
// would drift, and the failure mode is a user being offered a shortcut to a
// page they cannot open.
import { FaUserShield, FaFire, FaIdCard, FaHelicopter, FaRoute, FaCrosshairs, FaExclamationTriangle } from 'react-icons/fa'
import { TbDrone } from 'react-icons/tb'
import { isUasSpecialUser } from '@/utils/uas'

// Account type enums (matching backend).
//
// C# `UserAccountTypes` (FireSyncPortal.Models/UserAccount.cs L10-23), in
// declaration order — the wire value is the ordinal: Standard=0,
// SharedTrainingCal=1, SharedHouse=2, Sponsor=3, Advertiser=4, Reports=5.
// The port carried SharedTrainingCal as 3 for a month (audit 12h,
// OUTSTANDING §C57): a shared training-cal login was routed like a Sponsor
// and a Sponsor was sent to /trainingcalshared. This object is THE source of
// truth — do not redeclare it locally in a page.
export const AccountTypes = {
    Standard: 0,
    SharedTrainingCal: 1,
    SharedHouse: 2,
    Sponsor: 3,
    Advertiser: 4,
    Reports: 5,
}

export const OrgAccountTypes = {
    Unset: 0,
    Organization: 1,
    Private: 2,
    HouseOnly: 3,
    GroupApplet: 4,
    Sponsor: 5,
}

export const isAccountType = (org, type) => {
    if (!org) return false
    return org.accountType === AccountTypes[type]
}

export const isOrgAccountType = (org, type) => {
    if (!org) return false
    return org.orgAccountType === OrgAccountTypes[type]
}

/**
 * Hotspot Management access — the route-guard twin of the sidebar's Hotspots
 * link (owner ruling 2026-09-01, every org type): org admins, holders of the
 * `uas` applet permission (the UAS Admins roster — legacy's
 * Roles="uas, orgadmin" gate), and the two special users. Used by
 * /uas/hotspots and /uas/importhotspots so a link the sidebar shows always
 * opens.
 */
export const canAccessHotspots = (user, org) =>
    isUasSpecialUser(user)
    || !!org?.isAdmin
    || (org?.appletAdminPermissions || []).includes('uas')

// GroupApplet accounts get a reduced built-in set — legacy GroupMenu lists only
// these six and omits The House, The List, Training Calendar, Weather, Staffing
// and Board Finder. The UAS folder joins them too: legacy renders it from
// `SystemPortalApplets`, and PortalAuthService adds the `uas` SYSTEM applet
// for every org unconditionally — visibility is the role gate's job, never an
// org applet record (there is no server-side `uas` applet row to look for).
const GROUP_BUILT_INS = ['channels', 'calendar', 'helispots', 'maps', 'messageboard', 'radio', 'uas']

/**
 * Build the access-filtered built-in applet list for the current org + user.
 *
 * @param {object}   opts
 * @param {object}   opts.currentOrg
 * @param {function} opts.isModuleHidden  (moduleName) => boolean, from AuthContext
 * @param {boolean}  opts.isAdmin
 * @param {object}   opts.user
 * @returns {Array} nav items: { id, href?, label, iconPath, inFireSync, inFireOps, inEms }
 *                  plus `type: 'folder'` entries (UAS) and `isExternalSSO` entries
 *                  (Staffing Portal), neither of which is a plain route.
 */
export function buildBuiltInApplets({ currentOrg, isModuleHidden, isAdmin, user }) {
    if (!currentOrg) return []

    if (isAccountType(currentOrg, 'SharedTrainingCal') ||
        isAccountType(currentOrg, 'SharedHouse') ||
        isOrgAccountType(currentOrg, 'HouseOnly')) {
        return []
    }

    const items = []
    const orgId = currentOrg.orgId || currentOrg.OrgId

    // Legacy NavMenu / GroupMenu wrap every system applet's link in
    // `AuthorizeView Roles="<applet.Id>, orgadmin"` (NavMenu.razor L346, L391;
    // GroupMenu.razor L224). The `<applet.Id>` role is a claim minted from the
    // org's AppletAdminPermissions (PortalAuthService.cs L266-278), so a
    // built-in is reachable by org admins and by that applet's admins only —
    // the module-hide flags below narrow the org's set, this narrows the user's.
    const appletAdminPermissions = currentOrg.appletAdminPermissions || []
    const hasAppletRole = (appletId) => isAdmin || appletAdminPermissions.includes(appletId)

    if (!isModuleHidden('ChannelPlans') && hasAppletRole('channels')) {
        items.push({ id: 'channels', href: '/channels', label: 'Channel Plans', iconPath: 'global/channels.lacofd.png', inFireSync: false, inFireOps: true, inEms: false })
    }

    if (!isModuleHidden('EventCalendar') && hasAppletRole('calendar')) {
        items.push({ id: 'calendar', href: '/calendar', label: 'Event Calendar', iconPath: 'global/eventcal.png', inFireSync: true, inFireOps: true, inEms: true })
    }

    if (!isModuleHidden('Helispots') && hasAppletRole('helispots')) {
        const heliIcon = currentOrg.defaultHelispotsIcon || 'helispots.lacofd.png'
        items.push({ id: 'helispots', href: '/helispots', label: 'Helispots', iconPath: `global/${heliIcon}`, inFireSync: false, inFireOps: true, inEms: false })
    }

    // Legacy NavMenu.razor never reads HideMaps: for a standard org, Maps
    // falls through to the default applet block (L389-391) and only the role
    // gate applies. GroupMenu.razor L327 is the one reader of the flag
    // (`applet.Id == "maps" && !Auth.CurrentOrg.HideMaps`), so it narrows
    // GroupApplet orgs only (audit 12r).
    const isGroupOrg = isOrgAccountType(currentOrg, 'GroupApplet')
    if (!(isGroupOrg && isModuleHidden('Maps')) && hasAppletRole('maps')) {
        items.push({ id: 'maps', href: '/maps', label: 'Maps', iconPath: 'global/mapping.lacofd.png', inFireSync: false, inFireOps: true, inEms: false })
    }

    if (!isModuleHidden('MessageBoard') && hasAppletRole('messageboard')) {
        items.push({ id: 'messageboard', href: '/messageboard', label: 'Message Board', iconPath: 'global/messageboard.png', inFireSync: true, inFireOps: true, inEms: true })
    }

    const houseName = orgId === 'lacffassn' ? 'Fire House' : 'The House'
    items.push({ id: 'firehouseadmin', href: '/firehouseadmin', label: houseName, iconPath: 'global/firehouse.png', inFireSync: true, inFireOps: false, inEms: false })

    if (!isModuleHidden('LiveRadio') && hasAppletRole('radio')) {
        items.push({ id: 'radio', href: '/radio', label: 'Live Radio', iconPath: 'global/liveradio.png', inFireSync: false, inFireOps: true, inEms: false })
    }

    if (orgId === 'lacffassn' && hasAppletRole('trainingcal')) {
        items.push({ id: 'trainingcal', href: '/trainingcal', label: 'Training Calendar', iconPath: 'global/aoclist.png', inFireSync: true, inFireOps: false, inEms: false })
    }

    // ── UAS (Beta) ──
    // A collapsible folder rather than flat links, matching the legacy portal:
    // the Beta badge sits on the folder and the children drop the "UAS" prefix.
    // Legacy gating (AuthorizeView Roles="uas, orgadmin" — the `uas` role is a
    // claim minted from the user's AppletAdminPermissions, i.e. the roster on
    // the UAS Admins page): the folder needs the uas permission or orgadmin.
    // Hotspots opens to that whole folder audience ("matches outer gate",
    // owner ruling 2026-09-01); the other operational pages stay restricted
    // to two specific users.
    if (hasAppletRole('uas') || isUasSpecialUser(user)) {
        const uasChildren = []
        if (isAdmin) {
            uasChildren.push({ href: '/uas/admins', label: 'Admins', icon: FaUserShield, color: '#f0ad4e' })
        }
        if (isUasSpecialUser(user)) {
            uasChildren.push({ href: '/uas/incidents', label: 'Incidents', icon: FaFire, color: '#E65100' })
            uasChildren.push({ href: '/uas/pilots', label: 'Pilots', icon: FaIdCard, color: '#1565C0' })
            uasChildren.push({ href: '/uas/drones', label: 'Drones', icon: FaHelicopter, color: '#2E7D32' })
            uasChildren.push({ href: '/uas/missions', label: 'Missions', icon: FaRoute, color: '#1565C0' })
        }
        uasChildren.push({ href: '/uas/hotspots', label: 'Hotspots', icon: FaCrosshairs, color: '#C62828' })
        if (isUasSpecialUser(user)) {
            uasChildren.push({ href: '/uas/droneincidents', label: 'Safety Incidents', icon: FaExclamationTriangle, color: '#FF9800' })
        }
        if (uasChildren.length > 0) {
            items.push({
                id: 'uas',
                type: 'folder',
                key: 'uas-nav',
                // Client request 2026-09 (§C65): the sidebar title reads
                // "UAS/HotSpot Finder"; the nav id/key stay 'uas'.
                label: 'UAS/HotSpot Finder',
                // Dashboard shortcut tiles keep the short prefix ("UAS Hotspots",
                // "UAS Admins") — owner 2026-09-08: the long title is sidebar-only.
                shortLabel: 'UAS',
                iconPath: 'global/uas.png',
                fallbackIcon: TbDrone,
                badge: 'Beta',
                // UAS is an OPS-only feature, and legacy shows the OPS flame on
                // the folder header from the uas applet's InFireOps. This folder
                // is hardcoded rather than an applet record, so the flag is
                // stated here. NOTE: the sidebar's app filter applies only to
                // orgApplets, never to these built-ins, so this affects the
                // badge alone — it cannot hide UAS under a FireSync filter.
                inFireSync: false,
                inFireOps: true,
                inEms: false,
                children: uasChildren,
            })
        }
    }

    if (hasAppletRole('weather')) {
        items.push({ id: 'weather', href: '/weather', label: 'WX/FireBehavior', iconPath: 'global/weather.png', inFireSync: false, inFireOps: true, inEms: false })
    }

    // Legacy NavMenu gates the staffing link with Roles="staffing, orgadmin":
    // the `staffing` role is a claim minted from AppletAdminPermissions (the
    // Staffing Admin roster), so a staffing applet admin gets the handoff too.
    if (hasAppletRole('staffing')) {
        items.push({ id: 'staffing-portal', isExternalSSO: true, label: 'Go to Staffing Portal', iconPath: 'global/staffing.png', inFireSync: false, inFireOps: false, inEms: false })
    }

    if (orgId === 'lacffassn') {
        items.push({ id: 'boardfinder', href: '/boardfinder', label: 'BoardFinder', iconPath: 'global/boardfinder.png', inFireSync: true, inFireOps: false, inEms: false })
    }

    if (isGroupOrg) {
        return items.filter(i => GROUP_BUILT_INS.includes(i.id))
    }

    return items
}

/**
 * Flatten the nav list into pickable dashboard shortcuts.
 *
 * Only entries that are a real in-app route survive:
 *  - the **Staffing Portal** is an external SSO handoff with no `href`, so a
 *    tile for it would call `router.push(undefined)`;
 *  - the **UAS folder** itself has no `href` either — its children do, so they
 *    are offered individually, prefixed so "Pilots" is not ambiguous next to an
 *    org applet of the same name.
 *
 * Each option carries `refType`/`refId` — the identity persisted server-side.
 * For a built-in, `refId` is the stable key (`helispots`, `/uas/hotspots`), never
 * the label or href, both of which can change.
 *
 * @returns {Array} shortcut options: { id, refType, refId, label, sublabel, href, iconPath }
 */
export function builtInShortcutOptions(items = []) {
    const options = []

    for (const item of items) {
        if (item.type === 'folder') {
            for (const child of item.children || []) {
                if (!child.href) continue
                options.push({
                    id: `builtin:${child.href}`,
                    refType: 'BuiltIn',
                    // Folder children have no id of their own, so the route is the
                    // stable key.
                    refId: child.href,
                    label: `${item.shortLabel || item.label} ${child.label}`,
                    sublabel: 'Built-in',
                    href: child.href,
                    // Folder children have no icon image of their own; reuse the
                    // folder's so the tile is not a grey placeholder.
                    iconPath: item.iconPath || null,
                })
            }
            continue
        }

        if (!item.href) continue // external SSO, or anything else non-navigable

        options.push({
            id: `builtin:${item.id}`,
            refType: 'BuiltIn',
            refId: item.id,
            label: item.label,
            sublabel: 'Built-in',
            href: item.href,
            iconPath: item.iconPath || null,
        })
    }

    return options
}
